import { useContext } from "react"

import { 
  ErrorPlaceholder, 
  LoadingPlaceholder, 
  SimplePlaceholder 
} from "@components"

import { NotificationContext } from "@context"
import { GET_IMPORT_LOG_XLS_DOWNLOAD, GET_LATEST_IMPORT } from "@graphql"
import { downloadFile, mySqlToLocal } from "@helpers"
import { useLazyQuery, useQuery } from "@apollo/client"
import { Button } from "@heroui/react"
import { CircleStackIcon } from "@heroicons/react/24/outline"
import PropTypes from "prop-types"

const LastImportReport = ({ backButton, clientId }) => {
  const { notify } = useContext(NotificationContext)

  const { data: { client: { latestImport } = {}} = {}, error, loading } = useQuery(GET_LATEST_IMPORT, {
    variables: { id: clientId },
    fetchPolicy: "no-cache",
  });

  const [downloadReport, { loading: downloading }] = useLazyQuery(GET_IMPORT_LOG_XLS_DOWNLOAD, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => downloadFile(data.importLogDownload.filename, data.importLogDownload.url),
    onError: () => notify({ title: "Error", type: "danger", content: "There was a problem downloading your report. Please try again.", open: true })
  });

  if (loading) {
    return <LoadingPlaceholder classNames="mt-2" caption="Please wait. Loading data..." icon={CircleStackIcon} />;
  }

  if (error) {
    return (
      <ErrorPlaceholder 
        caption="There was a problem loading the import data. Please try again." 
        error={error}
      />
    )
  }

  if (!latestImport) {
    return (
      <SimplePlaceholder icon={CircleStackIcon} title="No Imports Yet">
        <p className="mt-1 text-sm text-gray-500">
          The ability to download the last import report will be available once an import has been performed.
        </p>
      </SimplePlaceholder>  
    )
  }

  if (latestImport?.status?.code === "FAILED") {
    return (
      <SimplePlaceholder icon={CircleStackIcon} title="Import Failed">
        <p className="mt-1 text-sm text-gray-500">
          The last import attempt failed. Please try re-importing your data to generate a report.
        </p>
      </SimplePlaceholder>
    )
  }

  if (latestImport?.status?.code === "RUNNING") {
    return (
      <SimplePlaceholder icon={CircleStackIcon} title="Import In Progress">
        <p className="mt-1 text-sm text-gray-500">
          An import is still in progress. Please check back later for the report.
        </p>
      </SimplePlaceholder>
    )
  }
  
  return (
    <div className="flex flex-col">
      <div className="w-full px-4 py-1 mb-4 text-center text-white rounded-full bg-success">
        Last Import Completed: 
        <span className="ml-4 font-semibold">
          {mySqlToLocal(latestImport.created_at).format("ddd MMM HH:mm YYYY")}
        </span>
      </div>
      
      <p className="mb-6">
        This report highlights all additions and removals made during the last import and is also useful for identifying any issues with guardian email issues.
      </p>

      <Button 
        className="font-semibold" 
        color="secondary" 
        fullWidth 
        onPress={() => downloadReport({ variables: { input: { client_id: clientId, import_id: latestImport?.id }}})} isLoading={downloading}
      >
        Download Report
      </Button>
      
      {backButton}
    </div>
  )
}

LastImportReport.propTypes = {
  backButton: PropTypes.node.isRequired,
  clientId: PropTypes.string.isRequired,
}

export default LastImportReport